import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios"
import type {
  Division,
  SidebarCounts,
  ContactSearchResult,
  SearchParams,
} from "../../types"
import { errorLog } from "../errorLog"
import { keys } from "../../keys"

// Error logging state - can be enabled/disabled based on user settings
// Initialize from localStorage so it persists across page reloads
const ERROR_LOGGING_KEY = "error_logging_enabled"
let errorLoggingEnabled = localStorage.getItem(ERROR_LOGGING_KEY) === "true"

export const setErrorLoggingEnabled = (enabled: boolean) => {
  errorLoggingEnabled = enabled
  // Persist to localStorage so it's available immediately on next page load
  if (enabled) {
    localStorage.setItem(ERROR_LOGGING_KEY, "true")
  } else {
    localStorage.removeItem(ERROR_LOGGING_KEY)
  }
}

export const isErrorLoggingEnabled = () => errorLoggingEnabled

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: keys.API_URL,
  timeout: 30000,
  withCredentials: true, // Send cookies for Passport session authentication
  headers: {
    "Content-Type": "application/json",
  },
})

// Track request start times for error logging
const requestStartTimes = new Map<string, number>()

// Request interceptor - add system API key for authorization
apiClient.interceptors.request.use(
  (config) => {
    // Use system API key like the original app (server uses Passport sessions + this key)
    config.headers.Authorization = `Bearer ${keys.SYSTEM_API_KEY}`

    // Track start time for error logging
    if (errorLoggingEnabled) {
      const requestId = `${config.method}-${config.url}-${Date.now()}`
      config.headers["X-Request-Id"] = requestId
      requestStartTimes.set(requestId, Date.now())
    }

    return config
  },
  (error) => Promise.reject(error)
)

// Activity tracking - store network logs for action detection
interface ActivityNetworkLog {
  timestamp: string
  type: "axios"
  url: string
  method: string
  status: number
  response?: string
}
const activityNetworkLogs: ActivityNetworkLog[] = []
const MAX_ACTIVITY_RESPONSE_LENGTH = 1500 // Enough to capture type field in notes response

export function getActivityNetworkLogs(): ActivityNetworkLog[] {
  const logs = [...activityNetworkLogs]
  activityNetworkLogs.length = 0
  return logs
}

// Response interceptor - handle errors and log requests
apiClient.interceptors.response.use(
  (response) => {
    // Log for activity tracking
    try {
      const responseStr = typeof response.data === "string"
        ? response.data
        : JSON.stringify(response.data)
      activityNetworkLogs.push({
        timestamp: new Date().toISOString(),
        type: "axios",
        url: response.config.url || "",
        method: (response.config.method || "GET").toUpperCase(),
        status: response.status,
        response: responseStr.slice(0, MAX_ACTIVITY_RESPONSE_LENGTH),
      })
    } catch {
      // Ignore logging errors
    }

    // Log successful request if error logging is enabled
    if (errorLoggingEnabled) {
      const requestId = response.config.headers?.["X-Request-Id"] as string
      const start = requestStartTimes.get(requestId) || Date.now()
      const end = Date.now()
      requestStartTimes.delete(requestId)
      errorLog.store(response, start, end)
    }

    // Check for API-level failure (success: false with 200 status)
    // The backend returns { success: false, message: [...] } for failures
    const data = response.data as { success?: boolean; message?: string[] }
    if (data && data.success === false) {
      const error = new Error(data.message?.[0] || "Request failed") as Error & { response: typeof response }
      error.response = response
      return Promise.reject(error)
    }
    return response
  },
  (error: AxiosError) => {
    // Log error if error logging is enabled
    if (errorLoggingEnabled) {
      const requestId = error.config?.headers?.["X-Request-Id"] as string
      const start = requestStartTimes.get(requestId) || Date.now()
      const end = Date.now()
      requestStartTimes.delete(requestId)
      errorLog.store(error, start, end)
    }

    return Promise.reject(error)
  }
)

// Generic request function
async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.request<T>(config)
  return response.data
}

// API endpoints organized by domain
export const api = {
  // Auth
  auth: {
    login: (email: string, password: string) =>
      request<{ token: string; user: any }>({
        method: "POST",
        url: "/v1/auth/login",
        data: { email, password },
      }),
    logout: () =>
      request<void>({
        method: "POST",
        url: "/v1/auth/logout",
      }),
    me: () =>
      request<{ data: any }>({
        method: "GET",
        url: "/v1/auth/me",
      }),
    viewingUser: () =>
      request<{ success: boolean; data: any }>({
        method: "GET",
        url: "/v1/auth/viewing_user",
      }),
    password: {
      update: (data: { user_id: string; password: string; passwordConfirm: string }) =>
        request<{ success: boolean; message?: string }>({
          method: "POST",
          url: "/v1/auth/password/update",
          data,
        }),
    },
    mfa: {
      send: (params: { _id: string; company: string; type: "phone" | "email" }) =>
        request<{ success: boolean }>({
          method: "GET",
          url: `/v1/auth/mfa`,
          params,
        }),
      set: (data: { user: string; type: "phone" | "email"; code: string }) =>
        request<{ success: boolean }>({
          method: "POST",
          url: "/v1/auth/mfa/set",
          data,
        }),
    },
  },

  // Matters
  matters: {
    search: (params: SearchParams) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/matters/search",
        data: params,
      }),
    searchList: (params?: { page?: number; limit?: number; search?: string; status?: string }) =>
      request<{ data: any[]; total: number }>({
        method: "GET",
        url: "/v1/matters/search",
        params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/matters/${id}`,
      }),
    overview: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/matters/${id}/overview`,
      }),
    overviewCommunication: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/matters/${id}/overview_communication`,
      }),
    related: (id: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/matters/${id}/related`,
      }),
    communicationAnalytics: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/matters/${id}/communication_analytics`,
      }),
    bookmarkAdd: (data: { matter: string }) =>
      request<{ success: boolean }>({
        method: "POST",
        url: "/v1/matters/bookmark/add",
        data,
      }),
    bookmarkRemove: (data: { matter: string }) =>
      request<{ success: boolean }>({
        method: "POST",
        url: "/v1/matters/bookmark/remove",
        data,
      }),
    create: (data: any) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/matters",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ data: any }>({
        method: "PATCH",
        url: `/v1/matters/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/matters/${id}`,
      }),
    restore: (id: string) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: `/v1/matters/${id}/restore`,
      }),
    moveStep: (id: string, data: { workflow_step: string; jump?: boolean; check?: boolean; matter_closing_reason?: string; matter_closing_reason_notes?: string }) =>
      request<{ success: boolean; message?: string[]; data?: { tasks?: any[]; moving_tasks?: any[] } }>({
        method: "PATCH",
        url: `/v1/matters/${id}/step`,
        data,
      }),
    // Party management
    partyAdd: (data: { matter: string; workflow_contact: string; contact: string }) =>
      request<{ success: boolean; data?: any }>({
        method: "PATCH",
        url: "/v1/matters/parties",
        data,
      }),
    partyRemove: (data: { matter: string; workflow_contact: string; contact: string }) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: "/v1/matters/parties",
        data,
      }),
    // Role management
    roleAdd: (data: { matter: string; workflow_role: string; user: string }) =>
      request<{ success: boolean; data?: any }>({
        method: "PATCH",
        url: "/v1/matters/roles",
        data,
      }),
    roleRemove: (data: { matter: string; workflow_role: string; user: string }) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: "/v1/matters/roles",
        data,
      }),
    // Contact time preferences
    setContactTime: (id: string, data: { contact: string; time: string }) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: `/v1/matters/${id}/contact_time`,
        data,
      }),
    // Tag management
    tagAdd: (data: { matter: string; tag: string }) =>
      request<{ success: boolean; data?: any }>({
        method: "PATCH",
        url: "/v1/matters/tags",
        data,
      }),
    tagRemove: (data: { matter: string; tag: string }) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: "/v1/matters/tags",
        data,
      }),
    // Location management
    locationAdd: (data: { matter: string; workflow_location: string; location: string; task?: string }) =>
      request<{ success: boolean; data?: any }>({
        method: "PATCH",
        url: "/v1/matters/locations",
        data,
      }),
    locationRemove: (data: { matter: string; workflow_location: string; location: string }) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: "/v1/matters/locations",
        data,
      }),
    // Custom date management (matter milestones)
    customDateAdd: (data: { matter: string; custom_date: string }) =>
      request<{ success: boolean; data?: any }>({
        method: "PATCH",
        url: "/v1/matters/custom_dates",
        data,
      }),
    // Expected date management
    expectedDateAdd: (data: { matter: string; expected_date: string; date: number; task?: string }) =>
      request<{ success: boolean; data?: any }>({
        method: "PATCH",
        url: "/v1/matters/expected_dates",
        data,
      }),
    // Alert management
    alertAdd: (data: { matter: string; body: string }) =>
      request<{ success: boolean; data?: any }>({
        method: "POST",
        url: "/v1/matters/alerts",
        data,
      }),
    alertResolve: (data: { matter: string; alert: string }) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: "/v1/matters/alerts",
        data,
      }),
    // Billing
    billing: (id: string) =>
      request<{ success: boolean; data: any }>({
        method: "GET",
        url: `/v1/matters/${id}/billing`,
      }),
    trust: (id: string, data: { billing_for_trust?: number }) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: `/v1/matters/${id}/trust`,
        data,
      }),
    // Lead sources management
    leadSources: (id: string, data: { lead_sources: Array<{ lead_source: string; lead_source_percent: number }> }) =>
      request<{ success: boolean; data?: any }>({
        method: "PATCH",
        url: `/v1/matters/${id}/lead_sources`,
        data,
      }),
  },

  // Payments
  payments: {
    charge: (data: {
      matter: string
      payment_method?: string
      amount: number
      type: "one_time" | "cash"
      date?: number
      description?: string
    }) =>
      request<{ success: boolean; data?: { amount_failed?: number; payments?: any[] }; message?: string[] }>({
        method: "POST",
        url: "/v1/payments",
        baseURL: keys.PROCESSING_URL,
        data,
      }),
    refund: (data: { payment: string; amount: number }) =>
      request<{ success: boolean; message?: string[] }>({
        method: "POST",
        url: "/v1/payments/refund",
        baseURL: keys.PROCESSING_URL,
        data,
      }),
    download: (matterId: string) =>
      apiClient.get(`/v1/payments/${matterId}/download`, { responseType: "blob" }),
  },

  // Payment Methods
  paymentMethods: {
    create: (data: { matter: string; token: string; owner_name: string; primary_method?: boolean; backup_method?: boolean }) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "POST",
        url: "/v1/payment_methods",
        data,
      }),
    update: (id: string, data: { primary_method?: boolean; backup_method?: boolean }) =>
      request<{ success: boolean; message?: string[] }>({
        method: "PATCH",
        url: `/v1/payment_methods/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean; message?: string[] }>({
        method: "DELETE",
        url: `/v1/payment_methods/${id}`,
      }),
    hosted: (matterId: string, processor: string) =>
      request<{ data: string }>({
        method: "GET",
        url: `/v1/payment_methods/${matterId}/hosted/${processor}`,
      }),
  },

  // Payment Subscriptions (Recurring Payment Plans)
  paymentSubscriptions: {
    create: (data: {
      matter: string
      amount: number
      interval: string
      start_date: string
      payment_method: string
      payment_method_backup?: string
      run_time: "morning" | "night"
    }) =>
      request<{ success: boolean; message?: string[] }>({
        method: "POST",
        url: "/v1/payment_subscriptions",
        data,
      }),
    update: (
      id: string,
      data: {
        matter: string
        amount: number
        interval: string
        next_run_date: string
        payment_method: string
        payment_method_backup?: string
        run_time: "morning" | "night"
      }
    ) =>
      request<{ success: boolean; message?: string[] }>({
        method: "PATCH",
        url: `/v1/payment_subscriptions/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean; message?: string[] }>({
        method: "DELETE",
        url: `/v1/payment_subscriptions/${id}`,
      }),
    resetPortalChanges: (matterId: string) =>
      request<{ success: boolean; message?: string[] }>({
        method: "POST",
        url: `/v1/payment_subscriptions/reset_portal_changes/${matterId}`,
      }),
  },

  // Invoices
  invoices: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/invoices/${id}`,
      }),
    create: (data: any) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "POST",
        url: "/v1/invoices",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean; message?: string[] }>({
        method: "PATCH",
        url: `/v1/invoices/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean; message?: string[] }>({
        method: "DELETE",
        url: `/v1/invoices/${id}`,
      }),
  },

  // Payment Trust Entries
  paymentTrustEntries: {
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/payment_trust_entries/${matterId}`,
      }),
    create: (data: {
      matter: string
      action_type: "payment" | "received"
      amount: number
      date: string
      payment_method: string
      reason: string
      party?: string
      party_address?: string
      payment?: string
      memo?: string
      check_number?: string
      check_series?: string
      check_date?: string
      check_from?: string
      transaction_reference?: string
      to_account_number?: string
    }) =>
      request<{ success: boolean; message?: string[] }>({
        method: "POST",
        url: "/v1/payment_trust_entries",
        data,
      }),
    reverse: (id: string, data?: { description?: string }) =>
      request<{ success: boolean; message?: string[] }>({
        method: "PATCH",
        url: `/v1/payment_trust_entries/${id}/reverse`,
        data,
      }),
  },

  // Workflow Contacts (parties assigned to matters)
  workflowContacts: {
    find: (workflowId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/workflow_contacts`,
        params: { workflow: workflowId },
      }),
  },

  // Workflow Roles (staff roles for matters)
  workflowRoles: {
    find: (workflowId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/workflow_roles`,
        params: { workflow: workflowId },
      }),
    search: (params: { searchString?: string; filter?: Record<string, any>; limit?: number }) =>
      request<{ data: { documents: any[] } }>({
        method: "POST",
        url: `/v1/workflow_roles/search`,
        data: params,
      }),
  },

  // Workflow Locations (locations for matters)
  workflowLocations: {
    find: (workflowId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/workflow_locations`,
        params: { workflow: workflowId },
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/workflow_locations/${id}`,
      }),
  },

  // Contacts
  contacts: {
    search: (params: SearchParams) =>
      request<{ data: { documents: ContactSearchResult[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/contacts/search",
        data: params,
      }),
    searchList: (params?: { page?: number; limit?: number; search?: string }) =>
      request<{ data: any[]; total: number }>({
        method: "GET",
        url: "/v1/contacts/search",
        params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/contacts/${id}`,
      }),
    overview: (id: string) =>
      request<{ data: { contact: any; matters: any[]; lastNote: any } }>({
        method: "GET",
        url: `/v1/contacts/${id}/overview`,
      }),
    create: (data: any) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/contacts",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/contacts/${id}`,
        data,
      }),
    divisions: (id: string, data: { divisions: any[] }) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/contacts/${id}/divisions`,
        data,
      }),
    password: (id: string, data: { password: string }) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/contacts/${id}/password`,
        data,
      }),
    getSocialSecurityNumber: (contactId: string) =>
      request<{ data: string }>({
        method: "GET",
        url: `/v1/social_security_numbers/${contactId}`,
      }),
  },

  // Notes
  notes: {
    find: (params: { contact?: string; matter?: string; type?: string; limit?: number; skip?: number }) =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/notes",
        params,
      }),
    create: (data: { value: string; type?: string; contact?: string; matter?: string }) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: "/v1/notes",
        data,
      }),
    update: (id: string, data: { value: string }) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/notes/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/notes/${id}`,
      }),
  },

  // Contact Types
  contactTypes: {
    find: () =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/contact_types",
      }),
  },

  // Social Security Numbers
  socialSecurityNumbers: {
    findByContact: (contactId: string) =>
      request<{ data: string }>({
        method: "GET",
        url: `/v1/social_security_numbers/${contactId}`,
      }),
    save: (contactId: string, data: { social_security_number: string }) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: `/v1/social_security_numbers/${contactId}`,
        data,
      }),
  },

  // Tasks
  tasks: {
    search: (data: {
      skip?: number
      limit?: number
      sort?: Record<string, number>
      filter: Record<string, unknown>
    }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/tasks/search",
        data,
      }),
    findByStep: (stepId: string, matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/tasks/by_step/${stepId}/${matterId}`,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/tasks/${id}`,
      }),
    create: (data: any) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/tasks",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ data: any }>({
        method: "PATCH",
        url: `/v1/tasks/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/tasks/${id}`,
      }),
    finish: (id: string, data?: any) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/tasks/${id}/finish`,
        data,
      }),
    recipients: (id: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/tasks/${id}/recipients`,
      }),
  },

  // Users
  users: {
    list: () =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/users",
      }),
    me: () =>
      request<{ data: any }>({
        method: "GET",
        url: "/v1/users/me",
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/users/${id}`,
      }),
    search: (params: { searchString?: string; filter?: Record<string, any>; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/users/search",
        data: params,
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/users/${id}`,
        data,
      }),
    divisionOverride: (id: string, data: { division: string; [key: string]: any }) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: `/v1/users/${id}/division_override`,
        data,
      }),
  },

  // Company/Config
  config: {
    company: () =>
      request<{ data: any }>({
        method: "GET",
        url: "/v1/config/company",
      }),
    divisions: () =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/config/divisions",
      }),
  },

  // Custom Fields
  customFields: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/custom_fields/${id}`,
      }),
    findByDivision: (divisionId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/custom_fields/${divisionId}/division`,
      }),
    search: (params: SearchParams) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/custom_fields/search",
        data: params,
      }),
  },

  // Custom Dates (matter milestone types for Set Tag/Date feature)
  customDates: {
    find: (divisionId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/custom_dates",
        params: { workflow: divisionId },
      }),
  },

  // Expected Dates (deadline types for Set Tag/Date feature)
  expectedDates: {
    find: (divisionId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/expected_dates",
        params: { workflow: divisionId },
      }),
  },

  // Custom Views
  customViews: {
    find: () =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/custom_views",
      }),
    findById: (id: string) =>
      request<{ success: boolean; data: any }>({
        method: "GET",
        url: `/v1/custom_views/${id}`,
      }),
  },

  // Documents
  documents: {
    search: (data: { filter: Record<string, unknown>; skip?: number; limit?: number; sort?: Record<string, number> }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/documents/search",
        data,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/documents/${id}`,
      }),
    update: (id: string, data: any) =>
      request<{ data: any }>({
        method: "PATCH",
        url: `/v1/documents/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/documents/${id}`,
      }),
    bulkDelete: (data: { documents: string[] }) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: "/v1/documents/bulk",
        data,
      }),
    pin: (data: { documents: string[]; pinned: boolean }) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: "/v1/documents/pin",
        data,
      }),
    // Upload goes to processing service (port 5002)
    upload: (formData: FormData) =>
      axios.post<{ success: boolean; data: any }>(`${keys.PROCESSING_URL}/v1/documents`, formData, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${keys.SYSTEM_API_KEY}`,
        },
      }),
    download: (id: string) =>
      request<{ data: string }>({
        method: "GET",
        url: `/v1/documents/${id}/download`,
      }),
  },

  // Matter Document Uploads (Categories)
  matterDocumentUploads: {
    find: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/matter_document_uploads",
        params: { matter: matterId },
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/matter_document_uploads/${id}`,
      }),
    search: (data: { filter: Record<string, unknown>; skip?: number; limit?: number }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/matter_document_uploads/search",
        data,
      }),
    create: (data: any) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/matter_document_uploads",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ data: any }>({
        method: "PATCH",
        url: `/v1/matter_document_uploads/${id}`,
        data,
      }),
    push: (categoryId: string, documentId: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/matter_document_uploads/${categoryId}/${documentId}`,
      }),
    pull: (categoryId: string, documentId: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/matter_document_uploads/${categoryId}/${documentId}`,
      }),
    queueValidation: (id: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/matter_document_uploads/${id}/queue_validation`,
      }),
    bulk: (data: { matter_document_upload: string; push?: string[]; pull?: string[] }) =>
      request<{ success: boolean }>({
        method: "POST",
        url: "/v1/matter_document_uploads/bulk",
        data,
      }),
  },

  // Signing Documents
  signingDocuments: {
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/signing/documents/${matterId}/matter`,
      }),
    search: (data: { filter: Record<string, unknown>; skip?: number; limit?: number }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/signing/documents/search",
        data,
      }),
    view: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/signing/documents/${id}/view`,
      }),
    findWithEvents: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/signing/documents/${id}/events`,
      }),
    create: (data: any) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/signing/documents",
        data,
      }),
    // Create signing document with file upload (for new PDFs)
    createWithFile: (formData: FormData) =>
      apiClient.post<{ success: boolean; data: any }>("/v1/signing/documents", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
    update: (id: string, data: any) =>
      request<{ data: any }>({
        method: "PATCH",
        url: `/v1/signing/documents/${id}`,
        data,
      }),
    void: (id: string, data?: any) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: `/v1/signing/documents/${id}`,
        data: { ...data, status: "void" },
      }),
    // Download signed document
    download: (id: string) =>
      apiClient.get(`/v1/signing/documents/${id}/download`, {
        responseType: "blob",
      }),
  },

  // Signing Templates
  signingTemplates: {
    search: (data: { filter?: Record<string, unknown>; skip?: number; limit?: number; searchString?: string }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/signing/templates/search",
        data,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/signing/templates/${id}`,
      }),
    create: (data: any) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/signing/templates",
        data,
      }),
    // Create signing template with file upload (for new templates)
    createWithFile: (formData: FormData) =>
      apiClient.post<{ success: boolean; data: any }>("/v1/signing/templates", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
    update: (id: string, data: any) =>
      request<{ data: any }>({
        method: "PATCH",
        url: `/v1/signing/templates/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/signing/templates/${id}`,
      }),
    getConfig: () =>
      request<{ data: { MAX_PARTIES: number; MAX_MARKUP_LENGTH: number; MAX_NAME_LENGTH: number; MAX_ANSWER_LENGTH: number } }>({
        method: "GET",
        url: "/v1/signing/templates/configuration",
      }),
    getFlatMatterData: (matterId: string) =>
      request<{ data: Record<string, any> }>({
        method: "GET",
        url: `/v1/signing/templates/flat_matter_data/${matterId}`,
      }),
    download: (id: string) =>
      apiClient.get(`/v1/signing/templates/${id}/download`, {
        responseType: "arraybuffer",
      }),
  },

  // Word Documents (templates)
  wordDocuments: {
    search: (data: { filter?: Record<string, unknown>; skip?: number; limit?: number }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/word_documents/search",
        data,
      }),
    generate: (templateId: string, matterId: string) =>
      // Returns arraybuffer for file download
      apiClient.get(`/v1/word_documents/${templateId}/${matterId}/generate`, {
        responseType: "arraybuffer",
      }),
  },

  // Automation Logs
  automationLogs: {
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/automation_logs/matter/${matterId}`,
      }),
  },

  // Automation Assignments (Linear Automations)
  automationAssignments: {
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/automation_assignments/matter/${matterId}`,
      }),
    create: (data: { automation: string; matter: string; created_by?: string }) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "POST",
        url: "/v1/automation_assignments",
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/automation_assignments/${id}`,
      }),
  },

  // State Automation Instances (Outreach Sequences)
  stateAutomationInstances: {
    findByMatter: (matterId: string) =>
      request<{ data: { active: any[]; paused: any[]; completed: any[]; resolved: any[] } }>({
        method: "GET",
        url: `/v1/state_automation_instances/matter/${matterId}`,
      }),
    getAvailable: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/state_automation_instances/available/${matterId}`,
      }),
    assign: (data: { template: string; matter: string }) =>
      request<{ success: boolean; message?: string[] }>({
        method: "POST",
        url: "/v1/state_automation_instances",
        data,
      }),
    pause: (id: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/state_automation_instances/${id}/pause`,
      }),
    resume: (id: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/state_automation_instances/${id}/resume`,
      }),
    remove: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/state_automation_instances/${id}`,
      }),
    restart: (id: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/state_automation_instances/${id}/restart`,
      }),
  },

  // Scheduled Texts
  scheduledTexts: {
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/scheduled_texts/matter/${matterId}`,
      }),
    create: (data: {
      contact: string
      to: string
      division: string
      matter: string
      body: string
      scheduled_for: number
      timezone: string
    }) =>
      request<{ success: boolean; data?: any }>({
        method: "POST",
        url: "/v1/scheduled_texts",
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/scheduled_texts/${id}`,
      }),
  },

  // Bankruptcy Module APIs
  modules: {
    bk: {
      // BK Cases - main case management
      cases: {
        findByMatter: (matterId: string) =>
          request<{ success: boolean; data: any }>({
            method: "GET",
            url: `/v1/modules/bk/cases/${matterId}`,
          }),
        info: (matterId: string) =>
          request<{ success: boolean; data: { garnishments: number; objections: number } }>({
            method: "GET",
            url: `/v1/modules/bk/cases/${matterId}/info`,
          }),
        save: (matterId: string, data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "POST",
            url: `/v1/modules/bk/cases/${matterId}`,
            data,
          }),
        update: (id: string, data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/cases/${id}`,
            data,
          }),
        moveStage: (matterId: string, data: { stage: string }) =>
          request<{ success: boolean; data?: { case: any }; message?: string[] }>({
            method: "POST",
            url: `/v1/modules/bk/cases/${matterId}/stage`,
            data,
          }),
        addHearingResult: (id: string, data: { type: string; outcome: string; notes?: string }) =>
          request<{ success: boolean; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/cases/${id}/hearing_result`,
            data,
          }),
      },
      // Docket Entries - court docket records from Pacer
      docketEntries: {
        find: (courtCode: string, caseNumber: string) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/docket_entries/${courtCode}/${caseNumber}`,
          }),
      },
      // Claims - creditor claims from Pacer
      claims: {
        find: (courtCode: string, caseNumber: string) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/claims/${courtCode}/${caseNumber}`,
          }),
      },
      // Court Drive - Pacer sync integration
      courtDrive: {
        sync: (data: { division: string; court_code: string; case_number: string; resource: "dockets" | "claims" }) =>
          request<{ success: boolean; message?: string[] }>({
            method: "POST",
            url: "/v1/modules/bk/court_drive/sync",
            data,
          }),
        sendDischargeCreditReports: (data: any) =>
          request<{ success: boolean; message?: string[] }>({
            method: "POST",
            url: "/v1/modules/bk/court_drive/send_discharge_credit_reports",
            data,
          }),
      },
      // Credit Reports
      creditReports: {
        findByMatter: (matterId: string) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/credit_reports/${matterId}`,
          }),
        download: (data: { document: string }) =>
          request<{ success: boolean; data: string }>({
            method: "POST",
            url: "/v1/modules/bk/credit_reports/download",
            data,
          }),
        pull: (data: { matter: string; contact: string; type: string }) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "POST",
            url: "/v1/modules/bk/credit_reports/pull",
            data,
          }),
      },
      // Creditors - full CRUD
      creditors: {
        findByMatter: (matterId: string) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/bk_creditors/${matterId}/matter`,
          }),
        findById: (id: string) =>
          request<{ success: boolean; data: any }>({
            method: "GET",
            url: `/v1/modules/bk/bk_creditors/${id}`,
          }),
        create: (data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "POST",
            url: "/v1/modules/bk/bk_creditors",
            data,
          }),
        update: (id: string, data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/bk_creditors/${id}`,
            data,
          }),
        delete: (id: string) =>
          request<{ success: boolean; message?: string[] }>({
            method: "DELETE",
            url: `/v1/modules/bk/bk_creditors/${id}`,
          }),
      },
      // Garnishments - full CRUD with sub-resources
      garnishments: {
        findByMatter: (matterId: string) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/bk_garnishments/${matterId}/matter`,
          }),
        findById: (id: string) =>
          request<{ success: boolean; data: any }>({
            method: "GET",
            url: `/v1/modules/bk/bk_garnishments/${id}`,
          }),
        create: (data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "POST",
            url: "/v1/modules/bk/bk_garnishments",
            data,
          }),
        update: (id: string, data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/bk_garnishments/${id}`,
            data,
          }),
        delete: (id: string) =>
          request<{ success: boolean; message?: string[] }>({
            method: "DELETE",
            url: `/v1/modules/bk/bk_garnishments/${id}`,
          }),
        notes: {
          add: (id: string, data: { body: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "POST",
              url: `/v1/modules/bk/bk_garnishments/${id}/note`,
              data,
            }),
        },
        checks: {
          add: (id: string, data: { date: string; memo?: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "POST",
              url: `/v1/modules/bk/bk_garnishments/${id}/check`,
              data,
            }),
          update: (id: string, data: { check_id: string; date?: string; memo?: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "PATCH",
              url: `/v1/modules/bk/bk_garnishments/${id}/check`,
              data,
            }),
          delete: (id: string, data: { check_id: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "DELETE",
              url: `/v1/modules/bk/bk_garnishments/${id}/check`,
              data,
            }),
        },
        documents: {
          add: (id: string, data: { document: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "POST",
              url: `/v1/modules/bk/bk_garnishments/${id}/document`,
              data,
            }),
          delete: (id: string, data: { document: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "DELETE",
              url: `/v1/modules/bk/bk_garnishments/${id}/document`,
              data,
            }),
        },
      },
      // Objections - full CRUD with sub-resources
      objections: {
        findByMatter: (matterId: string) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/bk_objections/${matterId}/matter`,
          }),
        findById: (id: string) =>
          request<{ success: boolean; data: any }>({
            method: "GET",
            url: `/v1/modules/bk/bk_objections/${id}`,
          }),
        create: (data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "POST",
            url: "/v1/modules/bk/bk_objections",
            data,
          }),
        update: (id: string, data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/bk_objections/${id}`,
            data,
          }),
        delete: (id: string) =>
          request<{ success: boolean; message?: string[] }>({
            method: "DELETE",
            url: `/v1/modules/bk/bk_objections/${id}`,
          }),
        notes: {
          add: (id: string, data: { body: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "POST",
              url: `/v1/modules/bk/bk_objections/${id}/note`,
              data,
            }),
        },
        documents: {
          add: (id: string, data: { document: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "POST",
              url: `/v1/modules/bk/bk_objections/${id}/document`,
              data,
            }),
          delete: (id: string, data: { document: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "DELETE",
              url: `/v1/modules/bk/bk_objections/${id}/document`,
              data,
            }),
        },
      },
      // Trustee Uploads - full CRUD with workflow actions
      trusteeUploads: {
        findByMatter: (matterId: string) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/bk_trustee_uploads/${matterId}/matter`,
          }),
        findById: (id: string) =>
          request<{ success: boolean; data: any }>({
            method: "GET",
            url: `/v1/modules/bk/bk_trustee_uploads/${id}`,
          }),
        create: (data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "POST",
            url: "/v1/modules/bk/bk_trustee_uploads",
            data,
          }),
        update: (id: string, data: any) =>
          request<{ success: boolean; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/bk_trustee_uploads/${id}`,
            data,
          }),
        delete: (id: string) =>
          request<{ success: boolean; message?: string[] }>({
            method: "DELETE",
            url: `/v1/modules/bk/bk_trustee_uploads/${id}`,
          }),
        search: (data: { filter?: Record<string, unknown>; skip?: number; limit?: number }) =>
          request<{ data: any[] }>({
            method: "POST",
            url: "/v1/modules/bk/bk_trustee_uploads/search",
            data,
          }),
        changeStage: (id: string, data: { stage: string }) =>
          request<{ success: boolean; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/bk_trustee_uploads/${id}/stage`,
            data,
          }),
        markUploaded: (id: string, data: { upload_confirmation_notes?: string }) =>
          request<{ success: boolean; message?: string[] }>({
            method: "POST",
            url: `/v1/modules/bk/bk_trustee_uploads/${id}/mark_uploaded`,
            data,
          }),
        markUnableToComplete: (id: string, data: { notes?: string }) =>
          request<{ success: boolean; message?: string[] }>({
            method: "POST",
            url: `/v1/modules/bk/bk_trustee_uploads/${id}/mark_unable_to_complete`,
            data,
          }),
        reassign: (id: string, data: { assigned_to: string[] }) =>
          request<{ success: boolean; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/bk_trustee_uploads/${id}/reassign`,
            data,
          }),
        notes: {
          add: (id: string, data: { body: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "POST",
              url: `/v1/modules/bk/bk_trustee_uploads/${id}/note`,
              data,
            }),
        },
      },
      // Module configuration
      config: () =>
        request<{ success: boolean; data: any }>({
          method: "GET",
          url: "/v1/modules/bk/config",
        }),
      // New Case Entries - recently filed cases from Pacer
      newCaseEntries: {
        find: (divisionId: string) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/new_case_entries/${divisionId}`,
          }),
        delete: (id: string) =>
          request<{ success: boolean; message?: string[] }>({
            method: "DELETE",
            url: `/v1/modules/bk/new_case_entries/${id}`,
          }),
      },
      // Item Type Configs - workflow-driven item types for stages
      itemTypeConfigs: {
        findByWorkflow: (workflowId: string, context: string, chapter?: number) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/item_type_configs/${workflowId}`,
            params: { context, chapter },
          }),
      },
      // Social Security Numbers - for credit report validation
      socialSecurityNumbers: {
        findByContact: (contactId: string) =>
          request<{ success: boolean; data: string | null }>({
            method: "GET",
            url: `/v1/social_security_numbers/${contactId}`,
          }),
      },
      // BK Supervisor Dashboard APIs
      supervisor: {
        daily: (divisionId: string, filter: { start: number; end: number }) =>
          request<{ success: boolean; data: any[] }>({
            method: "POST",
            url: `/v1/modules/bk/supervisor/${divisionId}/daily`,
            data: filter,
          }),
        outstandingItemSearch: (divisionId: string, params: any) =>
          request<{ data: { documents: any[]; total_documents: number } }>({
            method: "POST",
            url: `/v1/modules/bk/supervisor/${divisionId}/outstanding_item_search`,
            data: params,
          }),
        docketSearch: (divisionId: string, params: any) =>
          request<{ data: { documents: any[]; total_documents: number } }>({
            method: "POST",
            url: `/v1/modules/bk/supervisor/${divisionId}/docket_search`,
            data: params,
          }),
        newCaseEntries: (divisionId: string, params: any) =>
          request<{ data: { documents: any[]; total_documents: number } }>({
            method: "POST",
            url: `/v1/modules/bk/supervisor/${divisionId}/new_case_entries`,
            data: params,
          }),
        hearings: (divisionId: string, filter: { start: number; end: number }) =>
          request<{ success: boolean; data: any[] }>({
            method: "POST",
            url: `/v1/modules/bk/supervisor/${divisionId}/hearings`,
            data: filter,
          }),
        caseSearch: (divisionId: string, params: any) =>
          request<{ data: { documents: any[]; total_documents: number } }>({
            method: "POST",
            url: `/v1/modules/bk/supervisor/${divisionId}/case_search`,
            data: params,
          }),
        pendingReport: (divisionId: string, params: { chapter: number; bucket?: string; skip?: number; limit?: number; date_filed_start?: number; date_filed_end?: number }) =>
          request<{ success: boolean; data: { data: any[]; summary: any; total_documents: number; query_ms: number; skip: number; limit: number } }>({
            method: "POST",
            url: `/v1/modules/bk/supervisor/${divisionId}/pending_report`,
            data: params,
          }),
        caseTouch: (divisionId: string, params: { start: number; end: number; user_filter?: string; skip?: number; limit?: number }) =>
          request<{ success: boolean; data: { data: any[]; summary: any; total_documents: number; query_ms: number; skip: number; limit: number } }>({
            method: "POST",
            url: `/v1/modules/bk/supervisor/${divisionId}/case_touch`,
            data: params,
          }),
      },
      // BK Trustees
      trustees: {
        find: (divisionId: string, courtCode: string) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/trustees/${divisionId}/${courtCode}`,
          }),
        findById: (id: string) =>
          request<{ success: boolean; data: any }>({
            method: "GET",
            url: `/v1/modules/bk/trustees/${id}`,
          }),
        create: (data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "POST",
            url: "/v1/modules/bk/trustees",
            data,
          }),
        update: (id: string, data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/trustees/${id}`,
            data,
          }),
        delete: (id: string) =>
          request<{ success: boolean; message?: string[] }>({
            method: "DELETE",
            url: `/v1/modules/bk/trustees/${id}`,
          }),
      },
      // Trustee Uploads search all (global)
      trusteeUploadsGlobal: {
        search: (params: any) =>
          request<{ data: { documents: any[]; total_documents: number } }>({
            method: "POST",
            url: "/v1/modules/bk/bk_trustee_uploads/search_all",
            data: params,
          }),
        searchByMatter: (params: any) =>
          request<{ data: { documents: any[]; total_documents: number } }>({
            method: "POST",
            url: "/v1/modules/bk/bk_trustee_uploads/search_by_matter",
            data: params,
          }),
      },
      // BK Documents download
      documents: {
        download: (documentId: string) =>
          request<Blob>({
            method: "GET",
            url: `/v1/modules/bk/bk_documents/${documentId}/download`,
            responseType: "blob" as any,
          }),
      },
    },
  },

  // Claimables
  claimables: {
    find: (division: string) =>
      request<{ success: boolean; data: any[] }>({
        method: "GET",
        url: `/v1/claimables/${division}`,
      }),
    findAll: (division: string) =>
      request<{ success: boolean; data: any[] }>({
        method: "GET",
        url: `/v1/claimables/${division}/all`,
      }),
    claim: (id: string) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/claimables/${id}/claim`,
      }),
  },

  // Claimable Templates
  claimableTemplates: {
    find: () =>
      request<{ success: boolean; data: any[] }>({
        method: "GET",
        url: "/v1/claimable_templates",
      }),
  },

  // Call Center
  callCenter: {
    accessToken: () =>
      request<{ data: { token: string } }>({
        method: "GET",
        url: "/v1/call_center/access_token",
      }),
    matterInfo: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/call_center/${id}/matter`,
      }),
    contactInfo: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/call_center/${id}/contact`,
      }),
    addCall: (data: {
      user?: string | null
      contact?: string | null
      type: string
      userNumber?: string
      contactNumber?: string
      number: string
      callSid: string
    }) =>
      request<{ success: boolean; data?: any }>({
        method: "POST",
        url: "/v1/call_center/add_call",
        data,
      }),
    mergeCall: (data: { callSid: string; transferId?: string; type?: string; user?: string; contact?: string; number?: string }) =>
      request<{ success: boolean; data?: { status?: string } }>({
        method: "POST",
        url: "/v1/call_center/merge_call",
        data,
      }),
    cancelTransfer: (data: { callSid: string }) =>
      request<{ success: boolean }>({
        method: "POST",
        url: "/v1/call_center/cancel_transfer",
        data,
      }),
    hangup: (data: { callSid: string }) =>
      request<{ success: boolean }>({
        method: "POST",
        url: "/v1/call_center/hangup",
        data,
      }),
    hold: (data: { callSid: string }) =>
      request<{ success: boolean; data?: { status?: string } }>({
        method: "POST",
        url: "/v1/call_center/hold",
        data,
      }),
    checkTransferStatus: (callSid: string) =>
      request<{ success: boolean; data?: { answered: boolean } }>({
        method: "GET",
        url: `/v1/call_center/check_transfer_status?callSid=${callSid}`,
      }),
  },

  // Call Phone Numbers
  call_phone_numbers: {
    search: (params: {
      searchString?: string
      filter?: Record<string, any>
      limit?: number
      skip?: number
      sort?: Record<string, number>
    }) =>
      request<{ success: boolean; data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/call_phone_numbers/search",
        data: params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/call_phone_numbers/${id}`,
      }),
  },

  // Call Queue Entries
  callQueueEntries: {
    check: () =>
      request<{ data: any }>({
        method: "GET",
        url: "/v1/call_queue_entries/check",
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/call_queue_entries/${id}`,
      }),
  },

  // Call Offers
  callOffers: {
    decline: (callId: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/call_offers/${callId}/decline`,
      }),
    findByCall: (callId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/call_offers/${callId}/call`,
      }),
    findByUser: (userId: string, start: number, end: number) =>
      request<{ data: any[] }>({
        method: "PATCH",
        url: `/v1/call_offers/${userId}/user?start=${start}&end=${end}`,
      }),
  },

  // Call Handle Times
  callHandleTimes: {
    user: (data: { user: string; division: string; start: number; end: number; call_queue?: string }) =>
      request<{ data: { all: { total: number; duration: number; avg: number }; inbound: { total: number; duration: number; avg: number }; outbound: { total: number; duration: number; avg: number } } }>({
        method: "POST",
        url: "/v1/call_handle_times/user",
        data,
      }),
  },

  // Calls (Phone Call Logs)
  calls: {
    search: (params: {
      searchString?: string
      page?: number
      skip?: number
      sizePerPage?: number
      limit?: number
      filter?: Record<string, any>
      sort?: Record<string, number>
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/calls/search",
        data: params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/calls/${id}`,
      }),
    setReturned: (id: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/calls/${id}`,
      }),
  },

  // Call Flows
  call_flows: {
    search: (params: { searchString?: string; filter?: Record<string, any>; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/call_flows/search",
        data: params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/call_flows/${id}`,
      }),
  },

  // Call Queues
  call_queues: {
    search: (params: { searchString?: string; filter?: Record<string, any>; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/call_queues/search",
        data: params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/call_queues/${id}`,
      }),
  },

  // Call Queues (Queue Metrics)
  callQueues: {
    agentMetrics: (data: { start: number; end: number }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/call_queues/agent_metrics",
        data,
      }),
  },

  // Divisions
  divisions: {
    find: () =>
      request<{ data: Division[] }>({
        method: "GET",
        url: "/v1/divisions",
      }),
    search: (params: { searchString?: string; filter?: Record<string, any>; limit?: number }) =>
      request<{ data: { documents: Division[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/divisions/search",
        data: params,
      }),
  },

  // Division Departments
  divisionDepartments: {
    search: (params: { searchString?: string; filter?: Record<string, any>; limit?: number }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/division_departments/search",
        data: params,
      }),
  },

  // Workflows
  workflows: {
    find: () =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/workflows",
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/workflows/${id}`,
      }),
    search: (data: { filter?: Record<string, unknown>; searchString?: string; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/workflows/search",
        data,
      }),
  },

  // Pipelines
  pipelines: {
    findByUser: (userId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/pipelines/${userId}/user`,
      }),
    find: (division: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/pipelines",
        params: { division },
      }),
  },

  // Workflow Steps
  workflowSteps: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/workflow_steps/${id}`,
      }),
    search: (data: { filter?: Record<string, unknown>; skip?: number; limit?: number }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/workflow_steps/search",
        data,
      }),
  },

  // Workflow Step Categories
  workflow_step_categories: {
    search: (data: { filter?: Record<string, unknown>; searchString?: string; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/workflow_step_categories/search",
        data,
      }),
  },

  // Matter Closing Reasons
  matterClosingReasons: {
    findByWorkflow: (workflowId: string) =>
      request<{ success: boolean; data: Array<{ _id: string; name: string }> }>({
        method: "GET",
        url: `/v1/matter_closing_reasons/${workflowId}/workflow`,
      }),
  },

  // Dashboard
  dashboard: {
    sidebar: (divisionId: string) =>
      request<{ data: SidebarCounts }>({
        method: "GET",
        url: `/v1/dashboard/sidebar/${divisionId}`,
      }),
  },

  // Notifications
  notifications: {
    count: () =>
      request<{ data: { count: number } }>({
        method: "GET",
        url: "/v1/notifications",
      }),
    list: (params?: { limit?: number; skip?: number }) =>
      request<{ data: any[]; total: number }>({
        method: "GET",
        url: "/v1/notifications/list",
        params,
      }),
    markRead: (id: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/notifications/${id}/read`,
      }),
    markAllRead: () =>
      request<{ success: boolean }>({
        method: "POST",
        url: "/v1/notifications/mark_all_read",
      }),
  },

  // Load (app configuration)
  load: {
    standard: () =>
      request<{ data: { DOMAINS: Record<string, string>; PLATFORM?: any } }>({
        method: "GET",
        url: "/v1/load/standard",
      }),
  },

  // Query (batch operations)
  query: {
    batch: (collections: Record<string, string[]>) =>
      request<{ data: Record<string, any[]> }>({
        method: "POST",
        url: "/v1/query/batch",
        data: collections,
      }),
  },

  // Texts
  texts: {
    send: (data: {
      contact: string
      division: string
      to: string
      body?: string
      matter?: string
      task?: string
      attachments?: Array<{ data: string; type: string; name: string }>
    }) =>
      request<{ success: boolean; message?: string[] }>({
        method: "POST",
        url: "/v1/texts",
        data,
      }),
  },

  // Text Threads
  textThreads: {
    search: (params: {
      filter?: { assigned?: boolean; unreturned?: boolean; division?: string }
      searchString?: string
      limit?: number
      skip?: number
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/text_threads/search",
        data: params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/text_threads/${id}`,
      }),
    findByContactDivision: (contactId: string, divisionId: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/text_threads/${contactId}/${divisionId}`,
      }),
    read: (id: string, data: { read: boolean }) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: `/v1/text_threads/${id}`,
        data,
      }),
    setAssignedUsers: (id: string, data: { assigned_users: string[] }) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: `/v1/text_threads/${id}/assigned_users`,
        data,
      }),
  },

  // Call Voicemails
  callVoicemails: {
    search: (params: {
      searchString?: string
      page?: number
      skip?: number
      sizePerPage?: number
      limit?: number
      filter?: Record<string, any>
      sort?: Record<string, number>
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/call_voicemails/search",
        data: params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/call_voicemails/${id}`,
      }),
    resolve: (id: string) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: `/v1/call_voicemails/${id}/resolve`,
      }),
  },

  // Text Templates
  textTemplates: {
    search: (data: { searchString?: string; limit?: number }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/text_templates/search",
        data,
      }),
  },

  // Inject Values (for template variable substitution)
  injectValues: {
    string: (data: { string: string; contact?: string; matter?: string; hideErrors?: boolean }) =>
      request<{ data: string }>({
        method: "POST",
        url: "/v1/inject_values/string",
        data,
      }),
  },

  // Leads
  leads: {
    search: (params: {
      searchString?: string
      page?: number
      skip?: number
      sizePerPage?: number
      limit?: number
      filter?: Record<string, any>
      sort?: Record<string, number>
      isCSV?: boolean
    }) =>
      request<{ success: boolean; data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/leads/search",
        data: params,
      }),
    findById: (id: string) =>
      request<{ success: boolean; data: any; message?: string[] }>({
        method: "GET",
        url: `/v1/leads/${id}`,
      }),
    claim: (id: string) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "POST",
        url: `/v1/leads/${id}/claim`,
      }),
  },

  // Nylas (Email/Calendar Sync)
  nylas: {
    login: (division: string) =>
      request<{ data: { url: string } }>({
        method: "GET",
        url: `/v1/nylas/login`,
        params: { division },
      }),
  },

  // Nylas Email Grants
  nylasEmailGrants: {
    find: () =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/nylas_email_grants",
      }),
  },

  // Booking Links
  bookingLinks: {
    findMine: () =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/booking_links/me",
      }),
    createMine: (data: { type: number; division: string }) =>
      request<{ success: boolean }>({
        method: "POST",
        url: "/v1/booking_links/me",
        data,
      }),
    search: (data: { division: string; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/booking_links/search",
        data,
      }),
  },

  // Company Documents (avatars, logos, etc.)
  companyDocuments: {
    search: (data: { filter?: Record<string, unknown>; skip?: number; limit?: number; sort?: Record<string, number> }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/company_documents/search",
        data,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/company_documents/${id}`,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/company_documents/${id}`,
      }),
    // Upload goes to processing service (port 5002)
    upload: (formData: FormData) =>
      axios.post<{ success: boolean; data: any; message?: string[] }>(`${keys.PROCESSING_URL}/v1/company_documents`, formData, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${keys.SYSTEM_API_KEY}`,
        },
      }),
  },

  // Emails
  emails: {
    compose: (data: { division: string; matter?: string; contact?: string; email_thread?: string; email_template?: string; task?: string }) =>
      request<{ success: boolean; data: any; message?: string[] }>({
        method: "POST",
        url: "/v1/emails/compose",
        data,
      }),
    send: (data: {
      from: string
      to: Array<{ name: string; email: string; _id?: string }>
      attachments: Array<{ content: string; content_type: string; filename: string; is_inline: boolean; size: number }>
      subject: string
      body: string
      task?: string
      matter?: string
      email_template?: string
      email_sender?: string
      reply_to_message_id?: string
    }) =>
      request<{ success: boolean; data?: { email: string; thread?: string; support_ticket?: string }; message?: string[] }>({
        method: "POST",
        url: "/v1/emails",
        data,
      }),
    getAttachments: (id: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/emails/${id}/attachments`,
      }),
  },

  // Email Templates
  emailTemplates: {
    search: (params: { searchString?: string; filter?: { workflow?: string; division?: string }; limit?: number; skip?: number }) =>
      request<{ success?: boolean; data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/email_templates/search",
        data: params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/email_templates/${id}`,
      }),
  },

  // Email Senders (SendGrid configurations)
  emailSenders: {
    search: (params: { searchString?: string; filter?: { division?: string }; limit?: number; skip?: number }) =>
      request<{ success?: boolean; data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/email_senders/search",
        data: params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/email_senders/${id}`,
      }),
  },

  // AI Email
  aiEmail: {
    regenerate: (data: { body: string; prompt: string; matter?: string; contact?: string }) =>
      request<{ success: boolean; data?: { email: { subject: string; body: string } }; message?: string[] }>({
        method: "POST",
        url: "/v1/ai/email/regenerate",
        data,
      }),
  },

  // Events
  events: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/events/${id}`,
      }),
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/events/${matterId}/matter`,
      }),
    create: (data: any) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: "/v1/events",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/events/${id}`,
        data,
      }),
    finish: (id: string, data?: any) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/events/${id}/finish`,
        data,
      }),
    search: (params: any) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/events/search",
        data: params,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/events/${id}`,
      }),
  },

  // Calendar Events
  calendarEvents: {
    calendar: (start: number, end: number, user: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/calendar_events",
        params: { start, end, user },
      }),
  },

  // Event Templates
  eventTemplates: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/event_templates/${id}`,
      }),
    search: (params: any) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/event_templates/search",
        data: params,
      }),
  },

  // Locations
  locations: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/locations/${id}`,
      }),
    search: (params: any) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/locations/search",
        data: params,
      }),
  },

  // Forms
  forms: {
    find: (division: string, workflow?: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/forms?division=${division}${workflow ? `&workflow=${workflow}` : ''}`,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/forms/${id}`,
      }),
    search: (params: { filter?: Record<string, unknown>; skip?: number; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/forms/search",
        data: params,
      }),
    request: (data: {
      form: string
      matter: string
      contact: string
      division: string
      sendEmail?: boolean
      sendText?: boolean
      finishTask?: string
    }) =>
      request<{ success: boolean; sentText?: boolean; sentEmail?: boolean; queued?: boolean }>({
        method: "POST",
        url: "/v1/forms/request",
        data,
      }),
  },

  // Form Answers (tracks form progress per contact)
  formAnswers: {
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/form_answers/${matterId}`,
      }),
    find: (formId: string, matterId: string, contactId: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/form_answers/${formId}/${matterId}/${contactId}`,
      }),
    search: (params: { filter?: Record<string, unknown>; skip?: number; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/form_answers/search",
        data: params,
      }),
  },

  // Form Submissions (completed forms)
  formSubmissions: {
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/form_submissions/${matterId}/matter`,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/form_submissions/${id}`,
      }),
    search: (params: { filter?: Record<string, unknown>; skip?: number; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/form_submissions/search",
        data: params,
      }),
  },

  // Snake_case aliases for CollectionSearch compatibility
  // (CollectionSearch uses collection names like "event_templates" not "eventTemplates")
  event_types: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/event_types/${id}`,
      }),
    search: (params: { searchString?: string; filter?: Record<string, any>; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/event_types/search",
        data: params,
      }),
  },

  event_templates: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/event_templates/${id}`,
      }),
    search: (params: any) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/event_templates/search",
        data: params,
      }),
  },

  // Tags
  tags: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/tags/${id}`,
      }),
    search: (params: { searchString?: string; filter?: Record<string, any>; limit?: number }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/tags/search",
        data: params,
      }),
  },

  // Lead Sources
  lead_sources: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/lead_sources/${id}`,
      }),
    search: (params: { searchString?: string; filter?: Record<string, any>; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/lead_sources/search",
        data: params,
      }),
  },

  // Outstanding Items
  outstandingItems: {
    findByMatter: (matterId: string, finished = false) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/outstanding_items/${matterId}/matter`,
        params: { finished },
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/outstanding_items/${id}`,
      }),
    search: (data: {
      filter: Record<string, unknown>
      skip?: number
      limit?: number
      sort?: Record<string, number>
      searchString?: string
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/outstanding_items/search",
        data,
      }),
    finish: (id: string, data?: any) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/outstanding_items/${id}/finish`,
        data,
      }),
    create: (data: any) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: "/v1/outstanding_items",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/outstanding_items/${id}`,
        data,
      }),
    unfinished: (id: string, data?: any) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/outstanding_items/${id}/unfinished`,
        data,
      }),
    bulkFinish: (ids: string[]) =>
      request<{ success: boolean; data: { success: string[]; failed: string[] } }>({
        method: "POST",
        url: "/v1/outstanding_items/bulk/finish",
        data: { ids },
      }),
  },

  // Outstanding Item Templates
  outstandingItemTemplates: {
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/outstanding_item_templates/${matterId}/matter`,
      }),
    search: (data: {
      filter?: Record<string, unknown>
      skip?: number
      limit?: number
      searchString?: string
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/outstanding_item_templates/search",
        data,
      }),
  },

  // General Items
  generalItems: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/general_items/${id}`,
      }),
    pending: (params?: { filter?: Record<string, any> }) =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/general_items/user/pending",
        params,
      }),
    search: (data: {
      filter: Record<string, unknown>
      skip?: number
      limit?: number
      sort?: Record<string, number>
      searchString?: string
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/general_items/search",
        data,
      }),
    create: (data: any) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: "/v1/general_items",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/general_items/${id}`,
        data,
      }),
    finish: (id: string, data?: { outcome?: string }) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/general_items/${id}/finish`,
        data,
      }),
    reopen: (id: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/general_items/${id}/reopen`,
      }),
  },

  // Workflow Links
  workflowLinks: {
    find: (workflowId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/workflow_links`,
        params: { workflow: workflowId },
      }),
  },

  // Matter Approvals
  matterApprovals: {
    search: (params: {
      searchString?: string
      page?: number
      skip?: number
      sizePerPage?: number
      limit?: number
      filter?: Record<string, any>
      sort?: Record<string, number>
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/matter_approvals/search",
        data: params,
      }),
    pending: () =>
      request<{ success: boolean; data: any[] }>({
        method: "GET",
        url: "/v1/matter_approvals/pending",
      }),
    returned: () =>
      request<{ success: boolean; data: any[] }>({
        method: "GET",
        url: "/v1/matter_approvals/returned",
      }),
    denied: () =>
      request<{ success: boolean; data: any[] }>({
        method: "GET",
        url: "/v1/matter_approvals/denied",
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/matter_approvals/${id}`,
      }),
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/matter_approvals/matter/${matterId}`,
      }),
    create: (data: any) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: "/v1/matter_approvals",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean; data: any; message?: string[] }>({
        method: "PATCH",
        url: `/v1/matter_approvals/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/matter_approvals/${id}`,
      }),
    addDocument: (id: string, documentId: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/matter_approvals/${id}/documents`,
        data: { document_id: documentId },
      }),
    removeDocument: (id: string, documentId: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/matter_approvals/${id}/documents/${documentId}`,
      }),
  },

  // Matter Approval Templates
  matterApprovalTemplates: {
    find: (params?: { workflow?: string; division?: string }) =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/matter_approval_templates",
        params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/matter_approval_templates/${id}`,
      }),
  },

  // Portal Client Requests
  portalClientRequests: {
    pending: (data?: { filter?: Record<string, unknown>; skip?: number; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/portal_client_requests/pending",
        data,
      }),
    search: (data: {
      filter?: Record<string, unknown>
      skip?: number
      limit?: number
      sort?: Record<string, number>
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/portal_client_requests/search",
        data,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/portal_client_requests/${id}`,
      }),
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/portal_client_requests/matter/${matterId}`,
      }),
    review: (id: string, data: { status: "approved" | "denied"; admin_note?: string }) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/portal_client_requests/${id}/review`,
        data,
      }),
  },

  // User Comments
  userComments: {
    find: (collection: string, id: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/user_comments/${collection}/${id}`,
      }),
    create: (data: {
      collection_name: string
      collection_id: string
      matter: string
      body: string
      tagged_users?: string[]
      parent_name?: string
    }) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: "/v1/user_comments",
        data: {
          ...data,
          tagged_users: data.tagged_users || [],
        },
      }),
    markSeen: (data: { collection_name: string; collection_id: string }) =>
      request<{ success: boolean }>({
        method: "POST",
        url: "/v1/user_comments/seen",
        data,
      }),
    update: (id: string, data: { body: string; tagged_users?: string[] }) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/user_comments/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean; data: any }>({
        method: "DELETE",
        url: `/v1/user_comments/${id}`,
      }),
  },

  // AI Features
  ai: {
    documents: {
      merge: (data: { matter: string; name: string; prompt?: string; documents: string[] }) =>
        request<{ success: boolean; data?: any; message?: string[] }>({
          method: "POST",
          url: "/v1/ai/documents/merge",
          data,
        }),
    },
  },

  // User Activity Tracking
  user_activity_slices: {
    save: (data: any) =>
      request<{ accepted: boolean }>({
        method: "POST",
        url: "/v1/user_activity_slices",
        data,
      }),
  },

  // Analytics/Reporting
  analyze: {
    matters: (data: {
      filter: Record<string, unknown>
      sort: { type: string; field?: string }
      range: { start: number; end: number }
      limit: number
      skip: number
      isCSV: boolean
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/analyze/matters",
        data,
      }),
  },

  // Supervisor API - Management Dashboard endpoints
  _supervisor: {
    // Overview endpoints
    todaysActivity: (division: string, data: { start: number; end: number; tag?: string }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/todays_activity`,
        data,
      }),
    todaysActivityItems: (
      division: string,
      data: { filter: Record<string, unknown>; collection: string }
    ) =>
      request<{ data: any[] }>({
        method: "POST",
        url: `/v1/supervisor/${division}/todays_activity/items`,
        data,
      }),
    monthToDate: (division: string, data: { start: number; end: number; comparison_start: number; comparison_end: number }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/month_to_date`,
        data,
      }),
    pipelineBreakdown: (division: string, data: Record<string, unknown>) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/pipeline_breakdown`,
        data,
      }),

    // User Activity/QC endpoints
    userActivity: (division: string, data: { user: string; start: number; end: number; types?: string[] }) =>
      request<{ data: { timeline: Array<{ _id: string; timeline_type: string; timeline_timestamp: number; matter?: string; contact?: string; user?: string; name?: string; value?: string; status?: string; body?: string; workflow_step?: string; unix_start?: number; unix_end?: number }> } }>({
        method: "POST",
        url: `/v1/supervisor/${division}/user_activity`,
        data,
      }),
    userLinearity: (
      division: string,
      data: {
        start: number
        end: number
        workflow: string
        workflow_role: string
        users: string[]
      }
    ) =>
      request<{
        data: Array<{
          user: string
          name: string
          stages: Array<{ name: string; num: number }>
        }>
        message?: string
      }>({
        method: "POST",
        url: `/v1/supervisor/${division}/user_linearity`,
        data,
      }),
    userActivityMinutes: (data: { user: string; start: number; end: number }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/user_activity_minutes`,
        data,
      }),
    userActivityDays: (data: { day: string; division: string }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/user_activity_days`,
        data,
      }),

    // Phone Calls
    phoneCalls: (division: string, data: { start: number; end: number; filter?: Record<string, unknown> }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/phone_calls`,
        data,
      }),
    phoneCallsUsers: (division: string, data: { start: number; end: number }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/phone_calls_users`,
        data,
      }),
    phoneCallsDivision: (division: string, data: { start: number; end: number }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/phone_calls_user`,
        data,
      }),
    phoneCallsUser: (division: string, userId: string, data: { start: number; end: number }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/phone_calls_user/${userId}`,
        data,
      }),

    phoneCallsHourly: (division: string, data: { start: number; end: number; granularity: string; call_queue: string }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/phone_calls_hourly`,
        data,
      }),

    // Matter/Case tracking
    mattersOverdue: (division: string, data: Record<string, unknown>) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/matters_overdue`,
        data,
      }),
    matterAlerts: (division: string, data: Record<string, unknown>) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/matter_alerts`,
        data,
      }),

    // Tasks and Items
    tasks: (division: string, data: { start?: number; end?: number; filter?: Record<string, unknown> }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/tasks`,
        data,
      }),
    events: (division: string, data: { start?: number; end?: number; filter?: Record<string, unknown> }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/events`,
        data,
      }),
    eventCalls: (division: string, data: { start: number; end: number }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/event_calls`,
        data,
      }),
    taskCalls: (division: string, data: { start: number; end: number }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/task_calls`,
        data,
      }),
    outstandingItems: (division: string, data: { start?: number; end?: number; filter?: Record<string, unknown> }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/outstanding_items`,
        data,
      }),

    // Expected Dates
    expectedDates: {
      find: (division: string, data: Record<string, unknown>) =>
        request<{ data: any }>({
          method: "POST",
          url: `/v1/supervisor/${division}/expected_dates`,
          data,
        }),
      update: (division: string, data: Record<string, unknown>) =>
        request<{ success: boolean }>({
          method: "PATCH",
          url: `/v1/supervisor/${division}/expected_dates`,
          data,
        }),
    },

    // Leads
    leads: (division: string, data: { start?: number; end?: number; filter?: Record<string, unknown>; limit?: number; skip?: number; sort?: Record<string, number> }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: `/v1/supervisor/${division}/leads`,
        data,
      }),

    // Communications
    callVoicemails: (division: string, data: { start?: number; end?: number; filter?: Record<string, unknown> }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/call_voicemails`,
        data,
      }),
    textThreads: (division: string, data: { start?: number; end?: number; filter?: Record<string, unknown> }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/text_threads`,
        data,
      }),
    undeliveredTexts: (division: string, data: Record<string, unknown>) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/undelivered_texts`,
        data,
      }),
    notes: (division: string, data: { start?: number; end?: number; filter?: Record<string, unknown> }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/notes`,
        data,
      }),

    // Approvals and Requests
    matterApprovals: (division: string, data: { filter?: Record<string, unknown>; limit?: number; skip?: number; sort?: Record<string, number>; start?: number; end?: number; searchString?: string }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: `/v1/supervisor/${division}/matter_approvals`,
        data,
      }),
    portalClientRequests: (division: string, data: { filter?: Record<string, unknown>; limit?: number; skip?: number; sort?: Record<string, number>; start?: number; end?: number; searchString?: string }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: `/v1/supervisor/${division}/portal_client_requests`,
        data,
      }),

    // Documents
    matterDocumentUploads: (division: string, data: { start?: number; end?: number; filter?: Record<string, unknown> }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/matter_document_uploads`,
        data,
      }),
    uploads: (division: string, data: { start?: number; end?: number; filter?: Record<string, unknown>; skip?: number; limit?: number; searchString?: string; sort?: Record<string, number> }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/uploads`,
        data,
      }),

    // Bookings
    bookings: (division: string, data: { start?: number; end?: number; filter?: Record<string, unknown> }) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/bookings`,
        data,
      }),

    // Claimables (supervisor view)
    claimables: (division: string, data: Record<string, unknown>) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/claimables`,
        data,
      }),

    // Billing endpoints
    payments: (division: string, data: {
      start: number
      end: number
      filter?: Record<string, unknown>
      skip?: number
      limit?: number
      sort?: Record<string, number>
      isCSV?: boolean
    }) =>
      request<{ data: { documents: any[]; total_documents: number; overview: any } }>({
        method: "POST",
        url: `/v1/supervisor/${division}/payments`,
        data,
      }),
    paymentSubscriptions: (division: string, data: Record<string, unknown>) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/payment_subscriptions`,
        data,
      }),
    paymentSubscriptionsOverTime: (division: string, data: Record<string, unknown>) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/payment_subscriptions_over_time`,
        data,
      }),
    delinquentMatters: (division: string, data: Record<string, unknown>) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/delinquent_matters`,
        data,
      }),
    expiredMethods: (division: string, data: Record<string, unknown>) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/expired_methods`,
        data,
      }),
    revenueProjections: (division: string, data: Record<string, unknown>) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/revenue_projections`,
        data,
      }),
    mattersWithoutPlan: (division: string, data: Record<string, unknown>) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/matters_without_plan`,
        data,
      }),

    // Historical
    historical: {
      daily: (division: string, data: { start: number; dayOfWeek: string }) =>
        request<{ data: any }>({
          method: "POST",
          url: `/v1/supervisor/${division}/historical/daily`,
          data,
        }),
    },

    // Communication (Live)
    communication: {
      users: (division: string) =>
        request<{ data: any }>({
          method: "GET",
          url: `/v1/supervisor/${division}/communication/users`,
        }),
      queues: (division: string, start: number, end: number) =>
        request<{ data: any }>({
          method: "GET",
          url: `/v1/supervisor/${division}/communication/queues`,
          params: { start, end },
        }),
      calls: (division: string, data: Record<string, unknown>) =>
        request<{ data: any }>({
          method: "POST",
          url: `/v1/supervisor/${division}/communication/calls`,
          data,
        }),
      voicemail: (division: string, data: Record<string, unknown>) =>
        request<{ data: any }>({
          method: "POST",
          url: `/v1/supervisor/${division}/communication/voicemail`,
          data,
        }),
      activeQueueEntries: (division: string) =>
        request<{ data: any }>({
          method: "GET",
          url: `/v1/supervisor/${division}/communication/active_queue_entries`,
        }),
      callQueue: (division: string, queueId: string, data: Record<string, unknown>) =>
        request<{ data: any }>({
          method: "POST",
          url: `/v1/supervisor/${division}/communication/${queueId}`,
          data,
        }),
    },

    // Signing Field Audits
    signingFieldAudits: (division: string, data: Record<string, unknown>) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: `/v1/supervisor/${division}/signing_field_audits`,
        data,
      }),

    // Signatures
    signatures: {
      analytics: (
        division: string,
        data: {
          start: number
          end: number
          filter?: Record<string, unknown>
          sort?: Record<string, number>
        }
      ) =>
        request<{ data: { documents: any[]; total_documents: number } }>({
          method: "POST",
          url: `/v1/supervisor/${division}/signatures/analytics`,
          data,
        }),
    },

    // Support Tickets
    supportTickets: (division: string, data: Record<string, unknown>) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/supervisor/${division}/support_tickets`,
        data,
      }),

    // Time Tracking
    timeTracking: {
      timeEntries: (division: string, data: Record<string, any>) =>
        request<{ data: any }>({
          method: "POST",
          url: `/v1/supervisor/${division}/time_entries`,
          data,
        }),
      timeReports: (division: string, data: Record<string, any>) =>
        request<{ data: any }>({
          method: "POST",
          url: `/v1/supervisor/${division}/time_reports`,
          data,
        }),
      wipAging: (division: string, data: Record<string, any>) =>
        request<{ data: any }>({
          method: "POST",
          url: `/v1/supervisor/${division}/wip_aging`,
          data,
        }),
      activeTimers: (division: string) =>
        request<{ data: any }>({
          method: "GET",
          url: `/v1/supervisor/${division}/time_tracking_timers`,
        }),
      arAging: (division: string, data: Record<string, any>) =>
        request<{ data: any }>({
          method: "POST",
          url: `/v1/supervisor/${division}/ar_aging`,
          data,
        }),
      budgetReport: (division: string, data: Record<string, any>) =>
        request<{ data: any }>({
          method: "POST",
          url: `/v1/supervisor/${division}/budget_report`,
          data,
        }),
      activityOverview: (division: string, data: Record<string, any>) =>
        request<{ data: any }>({
          method: "POST",
          url: `/v1/supervisor/${division}/activity_overview`,
          data,
        }),
      entryPatterns: (division: string, data: Record<string, any>) =>
        request<{ data: any }>({
          method: "POST",
          url: `/v1/supervisor/${division}/entry_patterns`,
          data,
        }),
      activityReport: (division: string, data: Record<string, any>) =>
        request<{ data: any }>({
          method: "POST",
          url: `/v1/supervisor/${division}/activity_report`,
          data,
        }),
      timeEntriesByMatter: (division: string, data: Record<string, any>) =>
        request<{ data: any }>({
          method: "POST",
          url: `/v1/supervisor/${division}/time_entries_by_matter`,
          data,
        }),
      timeEntriesByCategory: (division: string, data: Record<string, any>) =>
        request<{ data: any }>({
          method: "POST",
          url: `/v1/supervisor/${division}/time_entries_by_category`,
          data,
        }),
      monthlyTimeReport: (division: string, data: Record<string, any>) =>
        request<{ data: any }>({
          method: "POST",
          url: `/v1/supervisor/${division}/monthly_time_report`,
          data,
        }),
      categoryBreakdown: (division: string, data: Record<string, any>) =>
        request<{ data: any }>({
          method: "POST",
          url: `/v1/supervisor/${division}/category_breakdown`,
          data,
        }),
    },

    // Training Oversight
    trainingOverview: (division: string, data?: Record<string, any>) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/supervisor/${division}/training_overview`,
        params: data,
      }),
    trainingUserProgress: (division: string, data?: Record<string, any>) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/supervisor/${division}/training_user_progress`,
        params: data,
      }),
    trainingRecentQuizResults: (division: string, params?: { page?: number; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "GET",
        url: `/v1/supervisor/${division}/training_recent_quiz_results`,
        params,
      }),
    trainingCourseStats: (division: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/supervisor/${division}/training_course_stats`,
      }),
  },

  // Signing Field Audits
  signingFieldAudits: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/signing_field_audits/${id}`,
      }),
    acknowledge: (id: string) =>
      request<{ data: any }>({
        method: "PATCH",
        url: `/v1/signing_field_audits/${id}/acknowledge`,
      }),
  },

  // Training Collections
  trainingEnrollments: {
    search: (data: Record<string, any>) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/training_enrollments/search",
        data,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/training_enrollments/${id}`,
      }),
    update: (id: string, data: Record<string, any>) =>
      request<{ data: any }>({
        method: "PATCH",
        url: `/v1/training_enrollments/${id}`,
        data,
      }),
  },
  trainingUnitProgress: {
    search: (data: Record<string, any>) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/training_unit_progress/search",
        data,
      }),
    unlockQuiz: (data: { unit_progress: string }) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/training_unit_progress/unlock_quiz",
        data,
      }),
  },
  trainingUnits: {
    search: (data: Record<string, any>) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/training_units/search",
        data,
      }),
  },
  trainingCourses: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/training_courses/${id}`,
      }),
  },


  aiFollowUpThreads: {
    search: (data: { filter?: Record<string, unknown>; skip?: number; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/ai_follow_up_threads/search",
        data,
      }),
    test: (data: Record<string, unknown>) =>
      request<{ success: boolean; data?: any }>({
        method: "POST",
        url: "/v1/ai_follow_up_threads/test",
        data,
      }),
  },

  aiCommunicationThreads: {
    search: (data: { filter?: Record<string, unknown>; skip?: number; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/ai_communication_threads/search",
        data,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/ai_communication_threads/${id}`,
      }),
    getResponse: (data: Record<string, unknown>) =>
      request<{ success: boolean; data?: any }>({
        method: "POST",
        url: "/v1/ai_communication_threads/get_response",
        data,
      }),
    setDirective: (id: string, data: Record<string, unknown>) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: `/v1/ai_communication_threads/${id}`,
        data,
      }),
  },

  aiHumanActions: {
    search: (data: { filter?: Record<string, unknown>; skip?: number; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/ai_human_actions/search",
        data,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/ai_human_actions/${id}`,
      }),
    resolve: (id: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/ai_human_actions/${id}/resolve`,
      }),
    summaryByUser: (data: Record<string, unknown>) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/ai_human_actions/summary/by_user",
        data,
      }),
    summaryByContact: (data: Record<string, unknown>) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/ai_human_actions/summary/by_contact",
        data,
      }),
  },

  // Support Messages (for portal messages)
  supportMessages: {
    create: (data: { matter: string; body: string }) =>
      request<{ success: boolean }>({
        method: "POST",
        url: "/v1/support_messages",
        data,
      }),
  },

  // Billing Categories
  billingCategories: {
    find: (division: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/billing_categories/${division}`,
      }),
  },

  // Time Entry Templates
  timeEntryTemplates: {
    find: (division: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/time_entry_templates/${division}`,
      }),
  },

  // Billing Automation Instances
  billingAutomationInstances: {
    list: (params: Record<string, unknown>) =>
      request<{ data: any[]; total: number }>({
        method: "GET",
        url: "/v1/billing_automation_instances",
        params,
      }),
    resume: (id: string) =>
      request<{ success: boolean }>({
        method: "PUT",
        url: `/v1/billing_automation_instances/${id}/resume`,
      }),
    resolve: (id: string) =>
      request<{ success: boolean }>({
        method: "PUT",
        url: `/v1/billing_automation_instances/${id}/resolve`,
      }),
    logCall: (id: string, body: { advance: boolean }) =>
      request<{ success: boolean }>({
        method: "PUT",
        url: `/v1/billing_automation_instances/${id}/log_call`,
        data: body,
      }),
  },

  // Time Entries (approval + supervisor management)
  timeEntries: {
    findById: (id: string) =>
      request<{ success: boolean; data?: any }>({
        method: "GET",
        url: `/v1/time_entries/${id}`,
      }),
    approve: (data: { time_entry_ids: string[] }) =>
      request<{ success: boolean; data?: any }>({
        method: "POST",
        url: "/v1/time_entries/approve",
        data,
      }),
    update: (id: string, data: Record<string, unknown>) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "PATCH",
        url: `/v1/time_entries/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean; message?: string[] }>({
        method: "DELETE",
        url: `/v1/time_entries/${id}`,
      }),
  },
  systemTickets: {
    create: (data: Record<string, unknown>) =>
      request<{ success: boolean; data: { _id: string } }>({
        method: "POST",
        url: "/v1/system_tickets/create",
        data,
      }),
    upload: (formData: FormData) =>
      request<{ success: boolean; data: { key: string; name: string; mimetype: string; size: number } }>({
        method: "POST",
        url: "/v1/system_tickets/upload",
        data: formData,
      }),
  },
}

export { apiClient }
